import React, { useCallback, useContext, useRef } from 'react';
import { Box, Theme, Typography, TypographyVariant } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import classNames from 'classnames';
import { SizeMe, SizeMeProps } from 'react-sizeme';
import {
  ContentSchema,
  OrganizationColorsSchema,
  RoomObjectAclSchema,
  RoomStructureContent as RoomStructureContentSchema,
  RoomStructureHeader,
} from '../../../definitions/schemas';
import { getContentDescription, getContentName } from './Content/helper';
import {
  getHeaderInfo,
  isContentTextBox,
} from '../../../lib/helper/RoomStructureHelper';
import { getTextBoxDisplayText } from './Content/TextBox/TextBoxHelper';
import { multilineEllipsis } from '../../../theme/styles/mixins';
import { OrganizationThemeContext } from '../../../lib/context/OrganizationThemeContext';
import { RoomContentContextProps } from '../../../lib/RoomContentContext';
import {
  RoomStructureFullWidthItemProps,
  RoomStructureItemsProps,
} from './RoomStructureItemProps';
import { RoomStructureItemType } from '../../../enums/RoomStructureItemType';
import ContentType, {
  isContentNotMentionable,
} from '../../../enums/ContentType';
import { RoomLayout, RoomContentMode } from '../../../enums/RoomLayout';
import { SimplifiedTrackingProps } from '../../Rooms/Tracking/types';
import RoomContentDetails, {
  RoomContentDetailsProps,
} from './Content/RoomContentDetails';
import RoomContentTile, {
  RoomContentTileProps,
} from './Content/RoomContentTile';
import SectionHeader from '../../SectionHeader/SectionHeader';
import ShowMoreLess from '../../Content/ShowMoreLess';
import TextDecorator from '../../TextDecorator';
import useFeatureFlag from '../../../lib/hook/useFeatureFlag';
import { OrganizationFlagEnum } from '../../../lib/features/FeatureFlag';
import BuyerContentAssetActions from './Content/BuyerContentAssetActions';
import { useBuyerContentAssetActionsStyles } from './Content/buyerContentAssetActionsStyles';
import { useCopilotMethods } from '../../GlobalCopilot';
import { TheaterModeContext } from './TheaterMode/TheaterModeContext';
import FolderLayout from '../../Folders/Layouts';
import { getAspectRatioHeight } from '../../../lib/Media';

const useStyles = makeStyles((theme: Theme) => ({
  contentCardWrapper: {
    position: 'relative',
  },
  multilineEllipsis: {
    ...multilineEllipsis(),
  },
  scrollTo: {
    position: 'absolute',
    scrollMargin: theme.spacing(6.25),
    top: theme.spacing(-4),
    [theme.breakpoints.down('lg')]: {
      scrollMargin: theme.spacing(8.75),
    },
    // scroll space for jump to section
    [theme.breakpoints.down('sm')]: {
      scrollMargin: theme.spacing(2),
      top: theme.spacing(-15),
    },
  },
}));

type PickedRoomContentDetailsProps = Pick<
  RoomContentDetailsProps,
  | 'actionable'
  | 'classes'
  | 'enableDocumentViewportDetection'
  | 'hideContactInformation'
  | 'RoomEmbeddableContentProps'
  | 'roomId'
  | 'trackingProperties'
>;

type PickedRoomContentTileProps = Pick<RoomContentTileProps, 'classes'>;

type ClassKey = 'descriptionMargin' | 'nameMargin';

export interface RoomStructureContentProps
  extends RoomStructureItemsProps,
    RoomStructureFullWidthItemProps {
  assignRef?: (id: string) => (el: HTMLDivElement) => void;
  theaterDeepLinkSectionId?: string;
  classes?: Partial<Record<ClassKey, string>>;
  contentObjectAcls?: RoomObjectAclSchema[];
  contextProps: RoomContentContextProps;
  disableMargin?: boolean;
  hideHeader?: boolean;
  index: number;
  itemId: string;
  mode?: RoomContentMode;
  onCopyAssetLink?: (sectionId: string) => void;
  orgColors?: OrganizationColorsSchema;
  referrer: string;
  renderContentAcl?: React.ReactNode;
  RoomContentDetailsProps: PickedRoomContentDetailsProps;
  RoomContentTileProps?: PickedRoomContentTileProps;
  sizeMeProps: SizeMeProps;
  TrackingProps?: SimplifiedTrackingProps;
  type: RoomStructureItemType;
  isBuyerCopilotEnabledForRoom?: boolean;
  // Folder support props
  roomId?: string;
  layout?: RoomLayout;
  onFolderClick?: (folderId: string) => void;
}

const RoomStructureContent = (
  props: RoomStructureContentProps
): JSX.Element => {
  const { buttonsStyles, textStyles } = useContext(OrganizationThemeContext);
  const { displayContentOnTheaterMode } = useContext(TheaterModeContext);

  const classes = useStyles();
  const assetActionStyles = useBuyerContentAssetActionsStyles();

  const copilotMethods = useCopilotMethods();

  const {
    assignRef,
    classes: classesProp,
    contextProps,
    disableMargin = false,
    hideHeader,
    index,
    itemId,
    mode = RoomContentMode.Default,
    onCopyAssetLink,
    orgColors,
    referrer,
    renderContentAcl,
    RoomContentDetailsProps,
    RoomContentTileProps,
    sizeMeProps,
    structureContents,
    structureHeaders,
    structureFolders,
    theaterDeepLinkSectionId,
    TrackingProps,
    type,
    isBuyerCopilotEnabledForRoom,
  } = props;

  const lastOpenedTheaterDeepLinkRef = useRef<string | undefined>(undefined);

  const isInlineIframeViewerEnabled = useFeatureFlag(
    OrganizationFlagEnum.INLINE_IFRAME_VIEWER
  );

  const handleCopilotIconClick = useCallback(
    (content: Partial<ContentSchema>) => {
      copilotMethods?.sendContentToCopilot({
        contentName: content.name ?? '',
        contentId: content.id?.toString() || '',
      });
    },
    [copilotMethods]
  );

  const getAssetActionsProps = useCallback(
    (
      structureContent: RoomStructureContentSchema,
      content: Partial<ContentSchema>,
      assetActionFlags: { showCopilot: boolean; showCopy: boolean }
    ) => ({
      onCopilotClick: () => handleCopilotIconClick(content),
      onCopyLink: onCopyAssetLink,
      sectionId: String(structureContent.id),
      showCopilot: assetActionFlags.showCopilot,
      showCopy: assetActionFlags.showCopy,
    }),
    [handleCopilotIconClick, onCopyAssetLink]
  );

  const getAssetActionFlags = (content: Partial<ContentSchema>) => ({
    showCopilot: !!(
      isBuyerCopilotEnabledForRoom &&
      !isContentNotMentionable(content.type as ContentType, content.mtData)
    ),
    showCopy: !!onCopyAssetLink,
  });

  const renderContentCard = (
    structureContent: RoomStructureContentSchema
  ): JSX.Element => {
    const { id, isDownloadable, match: content, url } = structureContent;

    const [desc, descVisible] = getContentDescription(
      structureContent,
      isInlineIframeViewerEnabled
    );
    const [name, nameVisible] = getContentName(
      structureContent,
      isInlineIframeViewerEnabled
    );

    const assetActionFlags = getAssetActionFlags(content);
    const hasActions =
      assetActionFlags.showCopilot || assetActionFlags.showCopy;
    const contentCardClassName = classNames(
      hasActions && classes.contentCardWrapper,
      hasActions && assetActionStyles.hoverReveal
    );

    const assetActionsProps = getAssetActionsProps(
      structureContent,
      content,
      assetActionFlags
    );

    return (
      <Box key={`room-content-${id}`} pb={2} className={contentCardClassName}>
        {assetActionFlags.showCopilot && (
          <BuyerContentAssetActions
            hasHeader={nameVisible}
            variant="card-copilot"
            {...assetActionsProps}
            showCopy={false}
          />
        )}
        {nameVisible && (
          <Typography
            className={classesProp?.nameMargin}
            paragraph
            component="h2"
            variant="h6"
          >
            {name}
          </Typography>
        )}
        {descVisible && (
          <Box mb={2}>
            <ShowMoreLess text={desc}>
              <TextDecorator
                component="div"
                disableAnchorIcon
                disableTrim
                text={desc}
                TypographyProps={{
                  className: nameVisible
                    ? undefined
                    : classesProp?.descriptionMargin,
                  variant: 'body2',
                }}
                whiteSpace="pre-wrap"
              />
            </ShowMoreLess>
          </Box>
        )}
        <Box position="relative">
          {assetActionFlags.showCopy && (
            <BuyerContentAssetActions
              variant="card-copy"
              {...assetActionsProps}
              showCopilot={false}
            />
          )}
          <RoomContentDetails
            {...TrackingProps}
            {...RoomContentDetailsProps}
            buttonsStyles={buttonsStyles}
            content={content}
            contentDescription={desc}
            contentName={name}
            contentUrl={url}
            contextProps={contextProps}
            disableImageContentName={!nameVisible}
            isDownloadable={isDownloadable}
            referrer={referrer}
            structureContent={structureContent}
            textStyles={textStyles}
            width={sizeMeProps.size?.width}
          />
        </Box>
      </Box>
    );
  };

  const renderContentTile = (
    structureContent: RoomStructureContentSchema
  ): JSX.Element => {
    const { id, match: content } = structureContent;
    const [name] = getContentName(structureContent, true);

    return (
      <Box key={`room-content-${id}`} pb={2}>
        <SizeMe refreshMode="debounce">
          {(sizeMeProps) => (
            <RoomContentTile
              {...TrackingProps}
              {...RoomContentTileProps}
              assetActionFlags={getAssetActionFlags(content)}
              content={content}
              onCopilotIconClicked={() => handleCopilotIconClick(content)}
              onCopyAssetLink={onCopyAssetLink}
              structureContent={structureContent}
              width={sizeMeProps.size.width}
            />
          )}
        </SizeMe>
        <Box mt={1}>
          <Typography
            className={classes.multilineEllipsis}
            title={name}
            variant="body2"
          >
            {name}
          </Typography>
        </Box>
      </Box>
    );
  };

  const renderContent = (
    structureContent: RoomStructureContentSchema
  ): JSX.Element => {
    const isFullWidth = structureContent.fullWidth;
    const displayAsTile = mode === RoomContentMode.Tile && !isFullWidth;

    const content = displayAsTile
      ? renderContentTile(structureContent)
      : renderContentCard(structureContent);

    return (
      <Box position="relative">
        {assignRef && (
          <Box
            ref={(el: HTMLDivElement | null) => {
              const id = String(structureContent.id);
              assignRef(id)(el as HTMLDivElement);
              if (!el) {
                return;
              }
              if (
                !theaterDeepLinkSectionId ||
                theaterDeepLinkSectionId !== id ||
                lastOpenedTheaterDeepLinkRef.current ===
                  theaterDeepLinkSectionId
              ) {
                return;
              }
              lastOpenedTheaterDeepLinkRef.current = id;
              displayContentOnTheaterMode(id);
            }}
            className={classes.scrollTo}
          />
        )}
        {renderContentAcl}
        {content}
      </Box>
    );
  };

  const renderHeader = (
    structureHeader: RoomStructureHeader,
    index: number
  ): JSX.Element => {
    if (hideHeader) {
      return <></>;
    }

    const { id } = structureHeader;

    const [name, icon, logo] = getHeaderInfo(structureHeader);

    const isFirstItem = index === 0;

    const isTileMode = mode === RoomContentMode.Tile;

    let variant: TypographyVariant = 'h5';
    if (isTileMode) {
      variant = 'h6';
    }

    return (
      <Box
        key={`room-section-header-${id}`}
        mb={disableMargin || isTileMode ? 0 : 4}
        mt={disableMargin || isFirstItem ? 0 : 4}
        position="relative"
      >
        {assignRef && (
          <Box {...{ ref: assignRef(id) }} className={classes.scrollTo} />
        )}
        <SectionHeader
          disableAvatar={!icon && !logo}
          icon={icon}
          logo={logo}
          name={name}
          orgColors={orgColors}
          variant={variant}
        />
      </Box>
    );
  };

  if (type === RoomStructureItemType.Folder) {
    // Find folder data from structureFolders prop
    const folderItem = structureFolders?.find((item) => item.id === itemId);

    if (!folderItem) {
      return <></>;
    }

    // Extract folder properties
    const { id = itemId, name, itemCount = 0, coverSigned } = folderItem;

    const isTiles = mode === RoomContentMode.Tile;

    return (
      <Box ref={assignRef?.(itemId)} pb={2}>
        <SizeMe refreshMode="debounce">
          {(sizeMeProps) => (
            <FolderLayout
              id={id}
              name={name}
              itemCount={itemCount}
              coverSigned={coverSigned}
              isTiles={isTiles}
              thumbnailHeight={
                isTiles && sizeMeProps.size.width
                  ? getAspectRatioHeight(sizeMeProps.size.width)
                  : undefined
              }
              onClick={() => {
                if (props.onFolderClick) {
                  props.onFolderClick(id);
                }
              }}
            />
          )}
        </SizeMe>
      </Box>
    );
  }

  if (type === RoomStructureItemType.Header) {
    const match = structureHeaders.find((item) => item.id === itemId);
    return match ? renderHeader(match, index) : <></>;
  }

  const match = structureContents.find(
    (item) => item.id === itemId && item.type === type
  );

  if (!match) {
    return <></>;
  }

  const isTextBox =
    type === RoomStructureItemType.TextBox || isContentTextBox(match.match);

  return renderContent(isTextBox ? getTextBoxDisplayText(match) : match);
};

export default RoomStructureContent;
